import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useAnnouncerContext } from "../context/AnnouncerContext";
import { useRingData } from "../context/RingDataContext";
import ClassResults from "./ClassResults";
import {
  isFollowingRing,
  computeReturnDot,
  classifyClassStatus,
  describeLookupClassHeading,
  resolveClassSponsorship,
} from "../utils/lookupColumn";
import { buildClassResults, classJudgesFromRideRows, isClassPlacedByDivision, isClassResultsFinal } from "../utils/orderOfGo";
import LookupRideList from "./LookupRideList";
import LookupHeading from "./LookupHeading";
import { formatClassNumber } from "../utils/formatClassNumber";

// #2142 (docs/score/announcer-ui-redesign.md §4.4/§5) — band 4, the look-up
// column: the only band that scrolls, showing exactly one class at a time.
// Consumes ONLY useAnnouncerContext()/useRingData() plus ClassResults itself
// (the card's one authorized new data source, #2141's `useClassResults`
// hook lives inside ClassResults already) — no new poll of its own.

// #2784: the horizontal gutter the sponsorship block shares with the ride rows.
const LOOKUP_GUTTER = 1.5;

export default function LookupColumn() {
  const { t } = useTranslation("announcer");
  const { viewingClassId, setViewingClassId, browserOnline } = useAnnouncerContext();
  const {
    classes,
    liveClassId,
    isClassPlacedFromSummary,
    rideRowsByClassId,
    resultsByClassId,
    handleClassResults,
    currentRideId,
    statusesUnknown,
    regionLoading,
    showId,
    selectedRing,
  } = useRingData();

  const viewedClassId = viewingClassId ?? liveClassId;
  const following = isFollowingRing(viewedClassId, liveClassId);

  // AC9's dot: latch a snapshot of {liveClassId, placed} at the moment
  // `following` flips false, cleared when it flips back true, so the dot's
  // "while away" comparison never drifts with the live class's own churn
  // once the announcer has already returned.
  const leftSnapshotRef = useRef(null);
  const wasFollowingRef = useRef(following);
  useEffect(() => {
    if (wasFollowingRef.current && !following) {
      leftSnapshotRef.current = {
        liveClassId,
        placed: isClassPlacedFromSummary(liveClassId),
      };
    } else if (!wasFollowingRef.current && following) {
      leftSnapshotRef.current = null;
    }
    wasFollowingRef.current = following;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [following]);

  // #2445 — every class this column has viewed or seen live. The visible
  // ClassResults below is keyed on the viewed class, so the moment the ring
  // (or a "back to ring" click) moves the view off a class it UNMOUNTS, and
  // #919's "keep polling until this class's OWN results are final" gate,
  // which lives inside ClassResults, dies with it. Each tracked class that
  // is not the viewed one keeps a render-less ClassResults mounted so that
  // same gate (untouched) keeps governing it: it polls while live or
  // not-yet-final, and settles to nothing once final.
  //
  // Bounded: the list is scoped to ONE [show, ring] (it restarts empty when
  // either changes) and only ids that are classes of the CURRENT ring render
  // a copy, so a class from a ring the announcer switched away from stops
  // being polled instead of lingering for the life of the page.
  const scope = `${showId ?? ""}|${selectedRing ?? ""}`;
  const [tracked, setTracked] = useState({ scope, ids: [] });
  useEffect(() => {
    const seen = [viewedClassId, liveClassId].filter(Boolean);
    setTracked((prev) => {
      const base = prev.scope === scope ? prev.ids : [];
      const next = seen.filter((id) => !base.includes(id));
      if (prev.scope === scope && !next.length) return prev;
      return { scope, ids: [...base, ...next] };
    });
  }, [scope, viewedClassId, liveClassId]);
  const ringClassIds = useMemo(
    () =>
      new Set(
        (classes || [])
          .filter((c) => String(c.ring) === String(selectedRing))
          .map((c) => c.id),
      ),
    [classes, selectedRing],
  );
  const trackedClassIds = tracked.scope === scope ? tracked.ids.filter((id) => ringClassIds.has(id)) : [];

  const returnDot = computeReturnDot({
    following,
    liveClassId,
    liveClassIdWhenLeft: leftSnapshotRef.current?.liveClassId ?? liveClassId,
    placedWhenLeft: leftSnapshotRef.current?.placed ?? false,
    placedNow: isClassPlacedFromSummary(liveClassId),
  });

  const liveClass = (classes || []).find((c) => c.id === liveClassId);
  const rideRows = rideRowsByClassId.get(viewedClassId) || [];
  const viewedResults = buildClassResults(resultsByClassId[viewedClassId] || [], rideRows);

  // #2584 (TRI-237) — the heading naming which class/test this panel is
  // showing. Derived from the VIEWED class (not the live one), so it tracks
  // whichever class the announcer is actually looking at, following or
  // pinned back.
  const { classLabel, testName } = describeLookupClassHeading({
    classes,
    classId: viewedClassId,
    rideRows,
  });

  // #2633 — the sponsor/prize for the class CURRENTLY IN THE RING (Jaybill's
  // description: "Show it for the class currently in the ring"), not
  // whichever class the announcer has pinned back to view — deliberately
  // liveClassId, not viewedClassId, unlike classLabel/testName above.
  const { sponsorName, sponsorTextLine, prize } = resolveClassSponsorship({
    classes,
    classId: liveClassId,
  });
  const hasSponsorship = Boolean(sponsorName || prize);

  const title = classLabel
    ? testName
      ? t("lookupColumn.classHeadingWithTest", { classLabel, testName })
      : t("lookupColumn.classHeading", { classLabel })
    : "";
  // One answer for "is the viewed class placed", shared by the heading and the
  // list so they cannot disagree: the ring-wide summary, OR every scored result
  // FINAL (FINAL placings are official even before the summary flips).
  const viewedPlaced =
    isClassPlacedFromSummary(viewedClassId) || isClassResultsFinal(resultsByClassId[viewedClassId]);
  const statusKind = classifyClassStatus({
    placed: viewedPlaced,
    rideRows,
    results: resultsByClassId[viewedClassId],
  });

  return (
    <Box
      data-testid="lookup-column"
      sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}
    >
      {/* #3393: the heading is fixed, outside the scroll box below. */}
      <LookupHeading
        following={following}
        title={title}
        placed={viewedPlaced}
        statusKind={statusKind}
        judges={classJudgesFromRideRows(rideRows)}
        backLabel={
          liveClass
            ? t("lookupColumn.backToClass", { classNumber: formatClassNumber(liveClass.classNumber) })
            : t("lookupColumn.backToRing")
        }
        returnDot={returnDot}
        onBack={() => setViewingClassId(null)}
      />

      {!classLabel && regionLoading.classes && (
        <Box data-testid="lookup-class-heading-loading" role="status" sx={{ px: LOOKUP_GUTTER, pt: 1, flexShrink: 0 }}>
          <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 700 }}>
            {t("lookupColumn.classHeadingLoading")}
          </Typography>
        </Box>
      )}

      {hasSponsorship && (
        <Box data-testid="lookup-sponsorship" sx={{ px: LOOKUP_GUTTER, pt: 0.5, pb: 0.5, flexShrink: 0 }}>
          {sponsorName && (
            <Typography data-testid="lookup-sponsor-name" variant="body2" sx={{ fontWeight: 600 }}>
              {t("lookupColumn.sponsorName", { name: sponsorName })}
            </Typography>
          )}
          {sponsorTextLine && (
            <Typography
              data-testid="lookup-sponsor-text-line"
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              {t("lookupColumn.sponsorTextLine", { textLine: sponsorTextLine })}
            </Typography>
          )}
          {prize && (
            <Typography data-testid="lookup-prize" variant="body2" color="text.secondary">
              {t("lookupColumn.prize", { prize })}
            </Typography>
          )}
        </Box>
      )}

      {/* #3394: the viewed class's results poller. It paints nothing: the merged
          list below is the one place results show. Unmounts with the view, as the
          old visible box did, so #2445's per-class gate is unchanged. */}
      <ClassResults
        key={viewedClassId}
        background
        classId={viewedClassId}
        isLive={viewedClassId === liveClassId}
        placed={isClassPlacedFromSummary(viewedClassId)}
        placedByDivision={isClassPlacedByDivision(classes, viewedClassId)}
        rideRows={rideRows}
        onResults={handleClassResults}
        online={browserOnline}
        statusesUnknown={statusesUnknown}
      />

      {trackedClassIds
        .filter((id) => id !== viewedClassId)
        .map((id) => (
          <ClassResults
            key={`bg-${id}`}
            background
            classId={id}
            isLive={id === liveClassId}
            placed={isClassPlacedFromSummary(id)}
            placedByDivision={isClassPlacedByDivision(classes, id)}
            rideRows={rideRowsByClassId.get(id) || []}
            onResults={handleClassResults}
            online={browserOnline}
            statusesUnknown={statusesUnknown}
          />
        ))}

      {/* #3394: the only scroller of the column. Heading and sponsorship sit above. */}
      <Box data-testid="lookup-scroll" sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <LookupRideList
          classId={viewedClassId}
          rideRows={rideRows}
          results={viewedResults}
          placed={viewedPlaced}
          placedByDivision={isClassPlacedByDivision(classes, viewedClassId)}
          currentRideId={currentRideId}
          loading={regionLoading.rides}
          isFinal={isClassResultsFinal(resultsByClassId[viewedClassId])}
        />
      </Box>
    </Box>
  );
}
